import { useCallback, useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';

type UseControllableStateParams<TState> = {
	controlledState?: TState;
	defaultState: TState;
	onChange?: (state: TState) => void;
};
/**
 * Setup the default uncontrolled state that will be used if the user of the component does not provide a value and control the state themselves.
 * ! This state should only be consumed in the `useControllableState` hook below and not used directly inside the UI components.
 */
function useUncontrolledState<TState>({
	defaultState,
	onChange = () => {}
}: Omit<UseControllableStateParams<TState>, 'state'>) {
	const uncontrolledState = useState<TState>(defaultState);

	const [value] = uncontrolledState;
	const prevValueRef = useRef(value);
	const handleChange = useCallback(onChange, [onChange]);

	useEffect(() => {
		if (prevValueRef.current !== value) {
			handleChange(value);
			prevValueRef.current = value;
		}
	}, [value, prevValueRef, handleChange]);

	return uncontrolledState;
}

type SetStateFn<TState> = (prevState?: TState) => TState;

/**
 *
 * @title
 * useControllableState
 *
 * @description
 * The purpose of this hook is have one API to combine controlled and uncontrolled state for UI components.
 * This is to simplify writing UI components that can be used in both controlled and uncontrolled mode.
 * Using this you only need one state in your UI component and it will handle the rest for you as long as you pass the correct props.
 *
 * Design taken from Radix UI. Link to their implementation of this API: https://github.com/radix-ui/primitives/blob/main/packages/react/use-controllable-state/src/useControllableState.tsx
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useControllableState({
 * 		state: isOpenFromProps,
 * 		defaultState: defaultIsOpen,
 * 		onChange: onChangeHandler
 * });
 * ```
 */
function useControllableState<TState>({
	controlledState,
	defaultState,
	onChange = () => {}
}: UseControllableStateParams<TState>) {
	const [uncontrolledStateValue, setUncontrolledStateValue] = useUncontrolledState<TState>({
		defaultState,
		onChange
	});
	const isControlled = controlledState !== undefined;
	const stateValue = isControlled ? controlledState : uncontrolledStateValue;
	const handleChange = useCallback(onChange, [onChange]);

	const setStateValue: Dispatch<SetStateAction<TState> | ((prevState?: TState) => TState)> = useCallback(
		nextValue => {
			if (isControlled) {
				const setter = nextValue as SetStateFn<TState>;
				const value = typeof nextValue === 'function' ? setter(controlledState) : nextValue;

				if (value !== controlledState) {
					handleChange(value);
				}

				return;
			}

			setUncontrolledStateValue(nextValue);
		},
		[handleChange, isControlled, controlledState, setUncontrolledStateValue]
	);

	return [stateValue, setStateValue] as const;
}

export default useControllableState;
